import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import usePageTitle from '../hooks/usePageTitle';

export default function LoginPage() {
  const { t } = useTranslation(['pages', 'common', 'errors']);
  usePageTitle(t('pages:login.title'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const logoSrc = theme.palette.mode === 'dark' ? '/contacttel-logo-dark.png' : '/contacttel-logo-light.png';

  const resolveReturnTo = () => {
    const returnToParam = searchParams.get('returnTo');
    if (!returnToParam) {
      return '/';
    }
    const decoded = decodeURIComponent(returnToParam);
    if (decoded.startsWith('/')) {
      return decoded;
    }
    return '/';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(resolveReturnTo(), { replace: true });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || t('errors:auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 480,
          width: '100%',
          borderRadius: 4,
          border: `1px solid rgba(255, 45, 109, 0.3)`,
          background: theme.palette.mode === 'dark' ? '#120f18' : '#ffffff',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 25px 60px rgba(0, 0, 0, 0.75)'
              : '0 20px 40px rgba(0, 0, 0, 0.12)',
        }}
      >
        <CardContent
          sx={{
            p: 4,
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(255, 45, 109, 0.08), rgba(124, 58, 237, 0.06))'
                : 'linear-gradient(180deg, rgba(255, 45, 109, 0.06), rgba(124, 58, 237, 0.04))',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 0.5,
                borderRadius: 2,
              }}
            >
            <Box
              component="img"
              src={logoSrc}
              alt="Contact Tel"
              sx={{
                display: 'block',
                height: 56,
                width: 'auto',
              }}
            />
            </Box>
            <Typography
              color="text.secondary"
              sx={{ mt: 1, fontFamily: '"Inter", sans-serif' }}
            >
              {t('pages:login.subtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('common:auth.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              autoComplete="email"
              sx={{
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15, 15, 15, 0.04)',
              }}
            />
            <TextField
              fullWidth
              label={t('common:auth.password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              sx={{
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15, 15, 15, 0.04)',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? t('pages:login.hidePassword') : t('pages:login.showPassword')}
                      onClick={handleTogglePassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.6,
                textTransform: 'none',
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('common:auth.login')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
